/*
 * file: ImportProducts.tsx
 * Project: papa-bear
 * -----
 * Last Modified: 24 Jan 2022 5:21:15 pm
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { Button, message, Upload } from 'antd';
import { RcFile } from 'antd/lib/upload';
import { useFormikContext } from 'formik';
import lodash from 'lodash';
import { ProductSubType } from 'models/ProductSubType';
import { SalesOrderLinesPayloadModel, SalesOrderPayloadModel } from 'models/SalesOrderPayloadModel';
import { FC, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { setIsTableLoading } from 'redux/productsInfo/productsInfoSlice';
import { setImportLoadingMessage } from 'redux/salesOrder/ordersSlice';
import { v4 } from 'uuid';
import { read, utils } from 'xlsx';

// function sleep(ms: number): Promise<void> {
//   return new Promise((resolve) => setTimeout(resolve, ms));
// }

export const ImportProducts: FC = () => {
  const { setValues, values } = useFormikContext<SalesOrderPayloadModel>();
  const dispatch = useDispatch();
  const { pathname } = useLocation();
  const isPosted = pathname.includes('/posted');
  const [count, setCount] = useState(0);
  const [productsToImport, setProductsToImport] = useState<SalesOrderLinesPayloadModel[]>([]);

  const beforeUpload = async (file: RcFile): Promise<void> => {
    dispatch(setIsTableLoading(true));

    if (file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
      const workbook = read(await file.arrayBuffer(), { type: 'array' });

      for (const sheetName of workbook.SheetNames) {
        const excelData = utils.sheet_to_json<SalesOrderPayloadModel['lines'][number]>(workbook.Sheets[sheetName]);
        const importedProducts: SalesOrderLinesPayloadModel[] = [];

        const groupedProducts = lodash.groupBy(excelData, (product: SalesOrderLinesPayloadModel & { lineNumber: number }) => Math.floor((product as { lineNumber: number }).lineNumber));

        Object.values(groupedProducts).forEach((productGroup: any, index) => {
          const subLines: ProductSubType[] = productGroup.slice(1);

          const parentProduct = productGroup[0] as SalesOrderLinesPayloadModel;

          const { quantity, unitCost, unitPrice, conditionId, ownerId, dispositionId, vendorId, warehouseId, siteId } = parentProduct;

          // CHECKS FOR DUPLICATE SUBLINE LINE NUMBERS, UNIQUE PARENT LINE NUMBERS, VALID SUBLINE QUANTITIES
          const subLineErrorIndexes: number[] = [];

          const filteredSubLines = lodash
            .uniqBy(
              subLines.filter((subLine, i) => !subLineErrorIndexes.includes(i)),
              (ele) => ele.lineNumber
            )
            .filter((ele) => ele.lineNumber !== parentProduct.lineNumber || (ele.quantity && ele.quantity >= 1));

          const product = {
            ...{
              ...parentProduct,
              id: v4(),
              quantity: quantity ?? 1,
              unitCost: unitCost ?? 0,
              unitPrice: unitPrice ?? 0,
              ownerId: ownerId || '',
              conditionId: conditionId || '',
              dispositionId: dispositionId || '',
              vendorId: vendorId,
              warehouseId,
              siteId
            },
            subLines: filteredSubLines
          };

          importedProducts.push(product as SalesOrderLinesPayloadModel);
        });
        setProductsToImport(importedProducts);
      }

      return;
    }

    message.error('You can only import files with the extension .xlsx');
  };

  useEffect(() => {
    if (productsToImport.length && count !== productsToImport.length) {
      setTimeout(() => {
        dispatch(setImportLoadingMessage(`Importing ${count + 1}/${productsToImport.length}`));
        setValues((prevState) => ({
          ...prevState,
          lines: [...prevState.lines, productsToImport[count]]
        }));
        setCount(count + 1);
      }, 200);
    }
    if (count === productsToImport.length - 1) {
      dispatch(setIsTableLoading(false));
      setCount(0);
      setProductsToImport([]);
    }
  }, [count, productsToImport]);

  return (
    <Upload accept=".xlsx" showUploadList={false} beforeUpload={beforeUpload}>
      <Button disabled={isPosted}>Import</Button>
    </Upload>
  );
};
